/* A COLOUR FROM ITS NAME, when her palette has words and no codes.
 *
 * Her ask, 2026-10-27: Jenn's locked palette says "Deep Dusty Blue", "Warm
 * Sand/Peach Neutral", "Golden White Light" — and not one #code — so every
 * tool read nothing and drew her in black and white. "Can you interpret and
 * try to use those."
 *
 * So: the colour words in a name pick a hue, lightness and saturation; the
 * words around them (deep, dusty, warm, light…) move it. Free, instant, no
 * Brill call — and an approximation, which is why brandSkin says so in its
 * notes and Me → Brand asks before using them. Her exact colour is always one
 * edit away there.
 */

/* h: degrees, s and l: 0–1. Where a word could be a hue or a shade (gold,
   sand, charcoal), it carries its own lightness. */
const BASE = {
  black: { h: 0, s: 0, l: 0.1 }, white: { h: 40, s: 0.3, l: 0.97 }, ivory: { h: 48, s: 0.6, l: 0.94 },
  cream: { h: 42, s: 0.55, l: 0.91 }, beige: { h: 35, s: 0.35, l: 0.84 }, sand: { h: 32, s: 0.42, l: 0.8 },
  tan: { h: 32, s: 0.35, l: 0.64 }, taupe: { h: 30, s: 0.12, l: 0.55 }, stone: { h: 35, s: 0.08, l: 0.62 },
  grey: { h: 210, s: 0.04, l: 0.5 }, gray: { h: 210, s: 0.04, l: 0.5 }, silver: { h: 210, s: 0.05, l: 0.76 },
  charcoal: { h: 210, s: 0.12, l: 0.2 }, slate: { h: 210, s: 0.17, l: 0.4 }, graphite: { h: 210, s: 0.06, l: 0.27 },
  navy: { h: 220, s: 0.55, l: 0.24 }, blue: { h: 210, s: 0.55, l: 0.47 }, cobalt: { h: 215, s: 0.7, l: 0.42 },
  sky: { h: 200, s: 0.6, l: 0.72 }, teal: { h: 178, s: 0.65, l: 0.32 }, turquoise: { h: 175, s: 0.6, l: 0.5 },
  aqua: { h: 180, s: 0.55, l: 0.6 }, ocean: { h: 200, s: 0.45, l: 0.4 }, seafoam: { h: 160, s: 0.35, l: 0.72 },
  mint: { h: 150, s: 0.4, l: 0.78 }, sage: { h: 95, s: 0.15, l: 0.6 }, olive: { h: 70, s: 0.35, l: 0.38 },
  green: { h: 130, s: 0.4, l: 0.4 }, emerald: { h: 145, s: 0.6, l: 0.36 }, forest: { h: 135, s: 0.4, l: 0.25 },
  eucalyptus: { h: 155, s: 0.15, l: 0.55 }, moss: { h: 85, s: 0.3, l: 0.36 },
  yellow: { h: 50, s: 0.85, l: 0.6 }, butter: { h: 48, s: 0.8, l: 0.82 }, gold: { h: 43, s: 0.65, l: 0.6 },
  golden: { h: 43, s: 0.65, l: 0.6 }, mustard: { h: 45, s: 0.65, l: 0.47 }, honey: { h: 38, s: 0.7, l: 0.58 },
  amber: { h: 36, s: 0.75, l: 0.52 }, ochre: { h: 38, s: 0.6, l: 0.45 }, orange: { h: 25, s: 0.85, l: 0.55 },
  peach: { h: 25, s: 0.75, l: 0.82 }, apricot: { h: 28, s: 0.75, l: 0.74 }, coral: { h: 10, s: 0.75, l: 0.66 },
  terracotta: { h: 15, s: 0.45, l: 0.5 }, rust: { h: 16, s: 0.6, l: 0.4 }, clay: { h: 18, s: 0.35, l: 0.55 },
  copper: { h: 22, s: 0.6, l: 0.47 }, bronze: { h: 30, s: 0.5, l: 0.4 }, brown: { h: 25, s: 0.4, l: 0.3 },
  chocolate: { h: 22, s: 0.4, l: 0.24 }, espresso: { h: 22, s: 0.35, l: 0.17 }, mocha: { h: 25, s: 0.25, l: 0.4 },
  red: { h: 0, s: 0.7, l: 0.47 }, crimson: { h: 350, s: 0.75, l: 0.42 }, burgundy: { h: 345, s: 0.55, l: 0.28 },
  wine: { h: 345, s: 0.5, l: 0.3 }, maroon: { h: 0, s: 0.55, l: 0.27 }, berry: { h: 330, s: 0.5, l: 0.38 },
  pink: { h: 340, s: 0.65, l: 0.78 }, blush: { h: 350, s: 0.55, l: 0.88 }, rose: { h: 345, s: 0.45, l: 0.66 },
  magenta: { h: 320, s: 0.7, l: 0.47 }, mauve: { h: 300, s: 0.16, l: 0.6 }, plum: { h: 300, s: 0.35, l: 0.3 },
  purple: { h: 275, s: 0.45, l: 0.42 }, violet: { h: 270, s: 0.5, l: 0.55 }, lavender: { h: 265, s: 0.45, l: 0.82 },
  lilac: { h: 280, s: 0.35, l: 0.78 }, indigo: { h: 240, s: 0.45, l: 0.33 },
};

/* Words that move a colour rather than name one. */
const MODIFIERS = {
  deep: (c) => ({ ...c, l: c.l * 0.68 }), dark: (c) => ({ ...c, l: c.l * 0.62 }),
  rich: (c) => ({ ...c, s: Math.min(1, c.s * 1.2), l: c.l * 0.85 }),
  light: (c) => ({ ...c, l: c.l + (1 - c.l) * 0.45 }), pale: (c) => ({ ...c, l: c.l + (1 - c.l) * 0.55, s: c.s * 0.8 }),
  soft: (c) => ({ ...c, s: c.s * 0.75, l: c.l + (1 - c.l) * 0.2 }), pastel: (c) => ({ ...c, s: c.s * 0.7, l: c.l + (1 - c.l) * 0.5 }),
  muted: (c) => ({ ...c, s: c.s * 0.55 }), dusty: (c) => ({ ...c, s: c.s * 0.45, l: c.l + (0.55 - c.l) * 0.25 }),
  dusky: (c) => ({ ...c, s: c.s * 0.5, l: c.l * 0.85 }), smoky: (c) => ({ ...c, s: c.s * 0.4 }),
  bright: (c) => ({ ...c, s: Math.min(1, c.s * 1.25) }), vivid: (c) => ({ ...c, s: Math.min(1, c.s * 1.35) }),
  warm: (c) => ({ ...c, h: towards(c.h, 30, 0.2) }), cool: (c) => ({ ...c, h: towards(c.h, 210, 0.2) }),
};

function towards(h, target, k) {
  const d = ((target - h + 540) % 360) - 180;
  return (h + d * k + 360) % 360;
}

function hslToHex({ h, s, l }) {
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(Math.max(0, Math.min(1, v)) * 255).toString(16).padStart(2, "0");
  };
  return `${f(0)}${f(8)}${f(4)}`.toUpperCase();
}

/* Several colour words blend — "Charcoal-Blue" is a dark blue, "Sand/Peach"
   between the two — with the LAST word, the noun, counting most. */
function blend(cols) {
  if (cols.length === 1) return cols[0];
  const w = cols.map((_, i) => (i === cols.length - 1 ? 1.5 : 1));
  const total = w.reduce((a, b) => a + b, 0);
  /* Hue only counts from colours that have one — a grey has no opinion. */
  const hued = cols.map((c, i) => [c, w[i] * c.s]).filter(([, k]) => k > 0.01);
  let x = 0, y = 0;
  for (const [c, k] of hued) { x += Math.cos((c.h * Math.PI) / 180) * k; y += Math.sin((c.h * Math.PI) / 180) * k; }
  const h = hued.length ? ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360 : cols[cols.length - 1].h;
  return {
    h,
    s: cols.reduce((a, c, i) => a + c.s * w[i], 0) / total,
    l: cols.reduce((a, c, i) => a + c.l * w[i], 0) / total,
  };
}

/**
 * "Deep Dusty Blue" → "4E6878", or null when no colour word is in it.
 * @returns {string|null} six hex digits, upper case, no #
 */
export function colourFromName(name) {
  const words = String(name || "").toLowerCase().split(/[^a-z]+/).filter(Boolean);
  const cols = words.filter((w) => BASE[w]).map((w) => BASE[w]);
  if (!cols.length) return null;
  let c = blend(cols);
  /* A shade word counts only BEFORE a colour word: "Deep Dusty Blue" is a
     deep blue, but "Golden White Light" is light as in sunlight — the noun,
     not an instruction to go paler. */
  const lastColour = words.reduce((at, w, i) => (BASE[w] ? i : at), -1);
  words.forEach((w, i) => { if (MODIFIERS[w] && !BASE[w] && i < lastColour) c = MODIFIERS[w](c); });
  c = { h: c.h, s: Math.max(0, Math.min(1, c.s)), l: Math.max(0.04, Math.min(0.98, c.l)) };
  return hslToHex(c);
}

/* A line that NAMES one colour of the palette: "**Primary — Deep Dusty
   Blue:** The water tone …", "- Accent: Golden White Light — buttons".
   The bold head is the role and the name; what follows is what it is for. */
const LINE = /^\s*[-•*]*\s*\*\*([^*]+?)\*\*\s*[:—–-]?\s*(.*)$/;
const ROLE_HEAD = /^(primary|secondary|tertiary|accent|base|background|neutral|supporting|text|highlight|main|dominant|light|dark)\b/i;
const NOT_A_COLOUR = /^(colou?r palette|palette|brand feeling|why|what|core|design|how)\b/i;

/**
 * Every colour her palette NAMES, as paletteEntries' shape plus `named`.
 * Only for prose with no codes at all — a palette with codes is read from
 * its codes and nothing here runs.
 */
export function namedPaletteEntries(text) {
  const out = [];
  for (const raw of String(text || "").split(/\r?\n/)) {
    const m = LINE.exec(raw);
    if (!m) continue;
    const head = m[1].replace(/[:\s]+$/, "").trim();
    if (NOT_A_COLOUR.test(head) || !ROLE_HEAD.test(head)) continue;
    /* "Primary — Deep Dusty Blue" → role "Primary", name "Deep Dusty Blue". */
    const parts = head.split(/\s+[—–-]\s+|:\s*/);
    const role = parts.length > 1 ? parts[0] : "";
    const name = (parts.length > 1 ? parts.slice(1).join(" ") : head).trim();
    const hex = colourFromName(name);
    if (!hex || out.some((e) => e.hex === hex)) continue;
    out.push({ hex, name, role, after: m[2].trim() });
  }
  return out;
}
